<template>
  <div class="py-4 container-fluid">
    <ToastPrime position="bottom-left" />
    <div class="card">
      <div
        class="card-header bg-white py-3 d-flex flex-wrap align-items-center justify-content-between gap-3 border-bottom">
        <div class="d-flex align-items-center gap-2">
          <h5 class="mb-0 fw-bold">Draft List</h5>
        </div>
        <div></div>
        <div class="d-flex gap-2">
          <button type="button" class="mx-1 mb-0 btn btn-sm btn-outline-primary" @click="openFilterDialog">
            Filter
          </button>
          <button type="button" class="mx-1 mb-0 btn btn-sm btn-primary" @click="openAddModal">Add</button>
        </div>
      </div>

      <div style="height: 500px; overflow-y: auto;">
        <DataTable :value="table.onLoad ? dummyRows : table.data" removable-sort scrollable scroll-height="flex"
          table-style="min-width: 50rem" class="text-sm">
          <Column field="mcr_number" header="MCR Number" header-class="bg-light text-dark" sortable>
            <template v-if="table.onLoad" #body>
              <Skeleton></Skeleton>
            </template>
            <template v-else #body="slotProps">
              {{ slotProps.data && slotProps.data.mcr_number ? slotProps.data.mcr_number : '' }}
            </template>
          </Column>
          <Column field="subject" header="Subject" header-class="bg-light text-dark" sortable>
            <template v-if="table.onLoad" #body>
              <Skeleton></Skeleton>
            </template>
            <template v-else #body="slotProps">
              {{ slotProps.data && slotProps.data.subject ? slotProps.data.subject : '' }}
            </template>
          </Column>
          <Column field="type" header="Type" header-class="bg-light text-dark" sortable>
            <template v-if="table.onLoad" #body>
              <Skeleton></Skeleton>
            </template>
            <template v-else #body="slotProps">
              {{ formatType(slotProps.data && slotProps.data.type ? slotProps.data.type : '') }}
            </template>
          </Column>
          <Column field="status" header="Status" header-class="bg-light text-dark" sortable>
            <template v-if="table.onLoad" #body>
              <Skeleton></Skeleton>
            </template>
            <template v-else #body="slotProps">
              {{ slotProps.data && slotProps.data.status ? slotProps.data.status : '' }}
            </template>
          </Column>
          <Column field="created_at" header="Created At" header-class="bg-light text-dark" sortable>
            <template v-if="table.onLoad" #body>
              <Skeleton></Skeleton>
            </template>
            <template v-else #body="slotProps">
              {{ slotProps.data && slotProps.data.created_at ? formatDate(slotProps.data.created_at) : '' }}
            </template>
          </Column>
          <Column header="Actions" header-class="bg-light" :style="{ width: '150px' }"
            :body-style="{ width: '150px', padding: '0' }">
            <template #body="slotProps">
              <template v-if="table.onLoad">
                <Skeleton></Skeleton>
              </template>
              <template v-else>
                <ButtonPrime v-tooltip.bottom="'Update'" icon="pi pi-pencil" outlined rounded size="small" class="me-2"
                  :disabled="!(slotProps.data && slotProps.data.id)" @click="openEditModal(slotProps.data)" />
                <ButtonPrime v-tooltip.bottom="'Delete'" icon="pi pi-trash" outlined rounded severity="danger"
                  size="small" :disabled="!(slotProps.data && slotProps.data.id)" @click="deleteItem(slotProps.data)" />
              </template>
            </template>
          </Column>
          <template #empty>
            <div style="text-align: center; padding: 40px 0;">
              <img src="/no_data.png" alt="No Data" style="max-width: 330px; opacity: 0.7; margin-bottom: 0;" />
              <div style="color: #888; font-size: 1.1em; font-weight: 500;">No Data</div>
            </div>
          </template>
        </DataTable>
      </div>
      <div class="card-footer pb-0 d-flex justify-content-between align-items-center">
        <BasePagination v-model="table.page.current_pages" :page-count="table.page.total_pages || 1" size="sm"
          @click="changePage()" />
        <p>
          {{ (table.page.current_pages - 1) * table.page.per_pages + table.data.length }} of {{ table.total }}
        </p>
      </div>
    </div>
    <!-- Dialog Add (PrimeVue) -->
    <DialogPrime v-model:visible="modal.visible" modal :draggable="false" style="width: 30rem; margin: 10px;"
      class="mcr-draft-modal">
      <template #header>
        <div class="d-flex align-items-center gap-3">
          <div>
            <h5 class="mb-0 fw-bold text-dark">{{ modal.title }}</h5>
          </div>
        </div>
      </template>
      <div class="row g-4">
        <div class="col-12">
          <div class="row g-3">
            <div class="col-12">
              <label for="subject" class="form-label fw-semibold">Subject <span class="text-danger">*</span></label>
              <InputText id="subject" v-model="modal.data.subject" placeholder="Subject"
                :invalid="!!modal.errorMessage['subject']" fluid />
            </div>
            <div class="col-12">
              <label for="type" class="form-label fw-semibold">Type <span class="text-danger">*</span></label>
              <Dropdown id="type" v-model="modal.data.type" :options="typeOptions" option-label="label"
                option-value="value" placeholder="Choose Type" class="w-100" :invalid="!!modal.errorMessage['type']" />
            </div>
            <div v-if="isMassUpdateType" class="col-12">
              <label for="mass-update-type" class="form-label fw-semibold">Mass Update Type <span
                  class="text-danger">*</span></label>
              <Dropdown id="mass-update-type" v-model="modal.data.mass_update_type" :options="massUpdateTypeOptions"
                option-label="label" option-value="value" placeholder="Choose Mass Update Type" class="w-100"
                :invalid="!!modal.errorMessage['mass_update_type']" />
            </div>
            <div v-if="isInalumCompany" class="col-12">
              <label for="section" class="form-label fw-semibold">Departement <span class="text-danger">*</span></label>
              <AutoComplete id="section" v-model="modal.data.section" :suggestions="departementSuggestions"
                placeholder="Select Departement" :invalid="!!modal.errorMessage['section']" force-selection
                input-class="w-100" class="w-100" complete-on-focus @complete="searchDepartement"
                @focus="clearModalError('section')" />
            </div>
            <div v-if="isInalumCompany" class="col-12">
              <label for="sub-section" class="form-label fw-semibold">Section </label>
              <AutoComplete id="sub-section" v-model="modal.data.sub_section" :suggestions="subSectionSuggestions"
                placeholder="Select Section" :invalid="!!modal.errorMessage['sub_section']" force-selection
                input-class="w-100" class="w-100" complete-on-focus @complete="searchSubSection"
                @focus="clearModalError('sub_section')" />
            </div>
          </div>
        </div>
      </div>
      <template #footer>
        <div class="d-flex justify-content-end gap-2 pt-3 border-top w-100 mt-2 px-1">
          <ButtonPrime type="button" label="Cancel" severity="danger" raised size="small" class="px-4 border-0"
            @click="closeModal()" />
          <ButtonPrime type="button" severity="contrast" raised size="small" :disabled="modal.onLoading"
            label="Save MCR Draft" class="px-4" @click="saveCreate">
            <template v-if="modal.onLoading" #icon>
              <i class="fas fa-spinner fa-spin me-2"></i>
            </template>
          </ButtonPrime>
        </div>
      </template>
    </DialogPrime>

    <!-- Dialog Filter (PrimeVue) -->
    <DialogPrime v-model:visible="filterDialog.visible" modal style="width: 30rem; margin: 10px;"
      class="mcr-filter-modal">
      <template #header>
        <div class="d-flex align-items-center gap-3">
          <div>
            <h5 class="mb-0 fw-bold text-dark">Filter</h5>
          </div>
        </div>
      </template>
      <div class="row g-4">
        <div class="col-12">
          <div class="row g-3">
            <div class="col-12">
              <label for="filter-type" class="form-label fw-semibold">Type</label>
              <Dropdown id="filter-type" v-model="filterDialog.form.type" :options="typeOptions" option-label="label"
                option-value="value" placeholder="All Types" class="w-100" :show-clear="true" />
            </div>
            <div class="col-12">
              <label for="filter-subject" class="form-label fw-semibold">Subject</label>
              <InputText id="filter-subject" v-model="filterDialog.form.subject" placeholder="Subject" fluid />
            </div>
            <div class="col-12">
              <label for="filter-created-at" class="form-label fw-semibold">Created At</label>
              <Calendar id="filter-created-at" v-model="filterDialog.form.created_at_date" :show-icon="true"
                placeholder="YYYY-MM-DD" class="w-100" />
            </div>
          </div>
        </div>
      </div>
      <template #footer>
        <div class="d-flex justify-content-end gap-2 pt-3 border-top w-100 mt-2 px-1">
          <ButtonPrime type="button" label="Reset" severity="danger" raised size="small" class="px-4 border-0"
            @click="resetFilter()" />
          <ButtonPrime type="button" severity="contrast" raised size="small" label="Apply Filter" class="px-4"
            @click="applyFilter()" />
        </div>
      </template>
    </DialogPrime>
  </div>
</template>

<script>
import BasePagination from "@/components/BasePagination.vue";
import api from "@/helpers/api";
import draftListService from "@/services/materialCatalogingRequest/draftListService";
import formConfigService from '@/services/materialCatalogingRequest/formConfigService';
import manageAccountService from '@/services/manageAccount/manageAccountService';
import moment from "moment";
import { mapActions } from "vuex";

const getCompanyCode = () => {
  try { return JSON.parse(localStorage.getItem('userAuth') || '{}')?.user?.company_code || ''; } catch { return ''; }
};
// const getUserRoleNames = () => {
//   try {
//     const roles = JSON.parse(localStorage.getItem('userAuth') || '{}')?.userHasRoles || [];
//     return roles.map(r => (typeof r === 'string' ? r : r?.name)).filter(Boolean);
//   } catch { return []; }
// };
const getUsername = () => {
  try { return JSON.parse(localStorage.getItem('userAuth') || '{}')?.user?.username || ''; } catch { return ''; }
};
// const ANTAM_EXCLUDED_MCR_TYPES = ['MCR006', 'MCR008', 'MCR009'];

export default {
  components: { BasePagination },
  data() {
    return {
      table: {
        onLoad: true,
        total: 0,
        data: [],
        limit: 100,
        page: {
          current_pages: 1,
          per_pages: 10,
          total_pages: 1,
        },
        loadTimeout: null,
      },
      dummyRows: Array.from({ length: 10 }, () => ({})),
      modal: {
        visible: false,
        action: "",
        title: "",
        data: { subject: '', type: 'choose_type', mass_update_type: null, section: '', sub_section: '', department: '' },
        errorMessage: {},
        onLoading: false,
      },
      filterDialog: {
        visible: false,
        form: {
          type: this.$route.query.type || '',
          subject: this.$route.query.subject || '',
          created_at: this.$route.query.created_at || '',
          created_at_date: this.$route.query.created_at ? new Date(this.$route.query.created_at) : null,
        }
      },
      typeOptions: [],
      massUpdateTypeOptions: [
        { label: 'SINGLE', value: 'SINGLE' },
        { label: 'MULTIPLE', value: 'MULTIPLE' },
      ],
      departementSuggestions: [],
      sectionSuggestions: [],
      subSectionSuggestions: [],
      userSectionsCache: [],
      userSubSectionsCache: [],
    };
  },
  computed: {
    isMassUpdateType() {
      return String(this.modal.data.type || '').toUpperCase() === 'MCR007';
    },
    isInalumCompany() {
      return getCompanyCode() === 'INALUM';
    },
  },
  watch: {
    'modal.data.type'() {
      if (!this.isMassUpdateType) {
        this.modal.data.mass_update_type = null;
        delete this.modal.errorMessage['mass_update_type'];
      }
    },
    'modal.data.section'(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.modal.data.sub_section = '';
        this.modal.data.department = this.departmentOfSection(newVal);
        delete this.modal.errorMessage['sub_section'];
        this.sectionSuggestions = [];
      }
    },
  },
  mounted() {
    this.fetchTypeOptions();
    this.get();
  },
  methods: {
    ...mapActions("mcr", ["fetchMcrFormAccess"]),
    async fetchTypeOptions() {
      try {
        // MCR type yang boleh DIBUAT user: baris GeneralSetting `mcr_form_access` company user (difilter fetchMcrFormAccess)
        // yang memberi role user akses 'edit' di status 'draft' (getters mcr/getCreatableMcrTypes; status '*' ikut
        // dihitung kalau tidak ada baris draft untuk role itu). Dulu dari mcr_drafting_config (sudah tidak dipakai).
        await this.fetchMcrFormAccess();
        const types = this.$store.getters['mcr/getCreatableMcrTypes'] || [];
        // get description of mcr type code
        const response = await api(this, formConfigService.get('mcr_type'), false);
        const mcrTypes = response?.data?.data || [];
        // clean up the format
        this.typeOptions = types.map(type => {
          const found = mcrTypes.find(t => t.code === type);
          return {
            label: found ? `${found.code} - ${found.description}` : type,
            value: type
          };
        });
      } catch (error) {
        this.typeOptions = [];
        this.$toastNotifPrime('error', 'Error', 'Failed to get MCR Type');
      }
    },
    formatType(type) {
      if (!type) return "";
      const option = this.typeOptions.find(opt => opt.value === type);
      if (option) return option.label;

      const map = { 'mrp': 'UPDATE MRP', 'create-new': 'CREATE-NEW', 'flag-unflag': 'FLAG-UNFLAG', 'change-material': 'CHANGE-MATERIAL', 'block-unblock': 'BLOCK-UNBLOCK', 'extend': 'EXTEND', 'update-price': 'UPDATE-PRICE', 'mass-update-mrp': 'MASS UPDATE MRP', 'mcr007': 'MASS UPDATE MRP' };
      return map[type] || map[String(type).toLowerCase()] || type;
    },
    formatDate(dateString) {
      if (!dateString) return "";
      return moment(dateString).format("DD/MM/YYYY HH:mm");
    },
    extractCode(value) {
      if (!value) return '';
      if (value.includes(' - ')) return value.split(' - ')[0].trim();
      return value;
    },
    clearModalError(field) {
      delete this.modal.errorMessage[field];
    },
    // Response sections & sub-sections sama-sama berbentuk
    // { section_code|sub_section_code, description, detail }, dengan `detail`
    // dan `description` yang bisa bernilai null.
    sectionLabel(entry) {
      const code = entry?.detail?.code || entry?.section_code || entry?.sub_section_code || '';
      const desc = entry?.detail?.description || entry?.description || code;
      return code ? `${code} - ${desc}` : '';
    },
    // Field Departement menyimpan label section; kode departemennya sendiri hanya
    // ada di detail.department_code, jadi dicari balik dari label terpilih.
    departmentOfSection(label) {
      const found = this.userSectionsCache.find(s => this.sectionLabel(s) === label);
      return found?.detail?.department_code || '';
    },
    async fetchUserSections() {
      if (this.userSectionsCache.length) return this.userSectionsCache;
      const username = getUsername();
      if (!username) return [];
      try {
        const res = await api(this, manageAccountService.getSectionsByUsername(username), false);
        const rows = res?.data?.data || res?.data || [];
        this.userSectionsCache = Array.isArray(rows) ? rows : [];
        return this.userSectionsCache;
      } catch {
        return [];
      }
    },
    async searchDepartement(event) {
      const query = (event?.query || '').toLowerCase();
      const sections = await this.fetchUserSections();
      const list = sections.map(s => this.sectionLabel(s)).filter(Boolean);
      const uniq = [...new Set(list)];
      this.departementSuggestions = query ? uniq.filter(l => l.toLowerCase().includes(query)) : uniq;
    },
    async fetchUserSubSections() {
      if (this.userSubSectionsCache.length) return this.userSubSectionsCache;
      const username = getUsername();
      if (!username) return [];
      try {
        const res = await api(this, manageAccountService.getSubSectionsByUsername(username), false);
        const rows = res?.data?.data || res?.data || [];
        this.userSubSectionsCache = Array.isArray(rows) ? rows : [];
        return this.userSubSectionsCache;
      } catch {
        return [];
      }
    },
    async searchSection(event) {
      const query = (event?.query || '').toLowerCase();
      const sections = await this.fetchUserSections();
      let list = sections.map(s => this.sectionLabel(s)).filter(Boolean);
      if (this.modal.data.section) {
        const deptCode = this.extractCode(this.modal.data.section);
        const filtered = sections.filter(s => s.detail?.code === deptCode);
        if (filtered.length) {
          list = filtered.map(s => this.sectionLabel(s)).filter(Boolean);
        }
      }
      this.sectionSuggestions = query ? list.filter(l => l.toLowerCase().includes(query)) : list;
    },
    async searchSubSection(event) {
      const query = (event?.query || '').toLowerCase();
      const list = (await this.fetchUserSubSections()).map(s => this.sectionLabel(s)).filter(Boolean);
      this.subSectionSuggestions = query ? list.filter(l => l.toLowerCase().includes(query)) : list;
    },
    async openAddModal() {
      this.modal.visible = true;
      this.modal.title = 'Add MCR Draft';
      this.modal.data = { subject: '', type: '', mass_update_type: null, section: '', sub_section: '', department: '' };
      this.modal.errorMessage = {};
      this.modal.onLoading = false;
      this.departementSuggestions = [];
      this.sectionSuggestions = [];
      this.subSectionSuggestions = [];
      if (this.isInalumCompany) {
        const sections = await this.fetchUserSections();
        const labels = [...new Set(sections.map(s => this.sectionLabel(s)).filter(Boolean))];
        if (labels.length === 1) {
          this.modal.data.section = labels[0];
          this.modal.data.department = this.departmentOfSection(labels[0]);
        }
        const subSections = await this.fetchUserSubSections();
        if (subSections.length === 1) {
          this.modal.data.sub_section = this.sectionLabel(subSections[0]);
        }
      }
    },
    closeModal() {
      this.modal.visible = false;
      this.modal.data = { subject: '', type: '', mass_update_type: null, section: '', sub_section: '', department: '' };
      this.modal.errorMessage = {};
      this.modal.onLoading = false;
    },
    openFilterDialog() {
      this.filterDialog.visible = true;
    },
    resetFilter() {
      this.filterDialog.form = { type: '', subject: '', created_at: '', created_at_date: null };
    },
    applyFilter() {
      // Sinkronkan ke query dan refresh data
      const query = {
        ...(this.filterDialog.form.type ? { type: this.filterDialog.form.type } : {}),
        ...(this.filterDialog.form.subject ? { subject: this.filterDialog.form.subject } : {}),
        ...(this.filterDialog.form.created_at_date ? { created_at: moment(this.filterDialog.form.created_at_date).format('YYYY-MM-DD') } : {}),
      };
      this.$router.push({ query: Object.keys(query).length ? query : null });
      this.table.page.current_pages = 1;
      this.filterDialog.visible = false;
      const createdAt = this.filterDialog.form.created_at_date
        ? moment(this.filterDialog.form.created_at_date).format('YYYY-MM-DD')
        : undefined;
      const params = {
        type: this.filterDialog.form.type || undefined,
        subject: this.filterDialog.form.subject || undefined,
        created_at: createdAt,
        limit: this.table.limit,
        page: this.table.page.current_pages,
      };
      this.get(params);
    },
    openEditModal(data) {
      if (!data) return;
      // Fetch details first, then navigate to UserForm
      this.getDetailsAndNavigate(data);
    },
    async getDetailsAndNavigate(data) {
      try {
        // Navigate to UserForm with only ID to display data
        // this.$router.push({ name: 'User Form', query: { id }});
        this.$router.push({ name: 'MCR Form for Draft', params: { id: data.id, action: "edit", mcrCode: data.mcr_code } });
        // console.log("getDetailsAndNavigate", data)
      } catch (error) {
        this.$toastNotifPrime('error', 'Error', 'Failed to navigate to UserForm');
      }
    },
    async saveCreate() {
      this.modal.errorMessage = {};
      if (!this.modal.data.subject) this.modal.errorMessage['subject'] = 'Subject required';
      if (!this.modal.data.type) this.modal.errorMessage['type'] = 'Type required';
      if (this.isMassUpdateType && !this.modal.data.mass_update_type) {
        this.modal.errorMessage['mass_update_type'] = 'Mass Update Type required';
      }
      if (this.isInalumCompany) {
        if (!this.extractCode(this.modal.data.section)) this.modal.errorMessage['section'] = 'Departement required';
        // if (!this.extractCode(this.modal.data.sub_section)) this.modal.errorMessage['sub_section'] = 'Section required';
      }
      if (Object.keys(this.modal.errorMessage).length > 0) return;

      this.modal.onLoading = true;
      try {
        const payload = {
          subject: this.modal.data.subject,
          type: this.modal.data.type,
          ...(this.isMassUpdateType ? { mass_update_type: this.modal.data.mass_update_type } : {}),
          ...(this.modal.data.department ? { department: this.modal.data.department } : {}),
          ...(this.modal.data.section ? { section: this.extractCode(this.modal.data.section) } : {}),
          ...(this.modal.data.sub_section ? { sub_section: this.extractCode(this.modal.data.sub_section) } : {}),
        };
        const response = await api(this, draftListService.create(payload), true);
        this.modal.visible = false;

        // Jika berhasil create, langsung ke tampilan edit/update
        const created = response?.data?.data || response?.data || null;
        if (created && created.id) {
          this.$router.push({ name: 'MCR Form for Draft', params: { id: created.id, action: "edit", mcrCode: created.mcr_code } });
          return;
        }

        this.get();
      } catch (error) {
        this.handleErrorObjectNested(error);
      } finally {
        this.modal.onLoading = false;
      }
    },
    handleErrorObjectNested(error) {
      if (error?.response?.status === 422 && error.response.data?.errors) {
        const errs = error.response.data.errors;
        Object.keys(errs).forEach((k) => {
          this.modal.errorMessage[k] = Array.isArray(errs[k]) ? errs[k][0] : errs[k];
        });
        return;
      }
      if (error.response?.data?.code == 400 && error.response.data.data) {
        error.response.data.data.forEach((v) => {
          this.modal.errorMessage[v.path] = v.msg;
        });
      }
    },
    async get(paramsOverride) {
      this.table.onLoad = true;
      try {
        const response = await api(
          this,
          draftListService.list(paramsOverride ?? {
            type: this.$route.query.type || undefined,
            subject: this.$route.query.subject || undefined,
            created_at: this.$route.query.created_at || undefined,
            limit: this.table.limit,
            page: this.table.page.current_pages,
          }),
          false
        );
        const root = response?.data ?? null;
        let rows = [];
        let pagedMeta = {};

        if (Array.isArray(root)) {
          rows = root;
        } else if (root && typeof root === 'object' && Array.isArray(root.data)) {
          rows = root.data;
          pagedMeta = root;
        } else if (root && typeof root === 'object') {
          rows = [root];
        }

        this.table.data = Array.isArray(rows) ? rows : [];
        this.table.total = (typeof pagedMeta.total === 'number') ? pagedMeta.total : this.table.data.length;
        this.table.page.total_pages = (typeof pagedMeta.last_page === 'number') ? pagedMeta.last_page : 1;
        this.table.page.current_pages = (typeof pagedMeta.current_page === 'number') ? pagedMeta.current_page : 1;
        this.table.page.per_pages = (typeof pagedMeta.per_page === 'number') ? pagedMeta.per_page : this.table.limit;
      } catch (error) {
        this.table.data = [];
        this.table.total = 0;
        this.table.page.total_pages = 1;
      } finally {
        this.table.onLoad = false;
      }
    },
    changePage() {
      this.get();
    },
    async deleteItem(data) {
      const id = data?.id;
      if (!id) return;
      const result = await this.confirmDialog("You won't be able to revert this!", "delete it");
      if (!result.isConfirmed) return;
      try {
        await api(this, draftListService.delete(id), true);
        this.get();
      } catch (error) {
        // Toast sudah ditampilkan oleh helper api (showMessage=true). Jangan rethrow agar tidak memunculkan overlay error.
      }
    },
  },
};
</script>